const domTree = new WeakMap();
const configuration = new WeakMap();

/**
 * Component is a class that should be extended for every component that's being made. It
 * is a helper class to keep the code uniform.
 *
 * __PLEASE NOTE__: This is only to be extended, not instantiated.
 *
 * @example
 * import Component from 'component';
 *
 * class Foo extends Component {
 *   construction(el, config){
 *     super(el, config);
 *   }
 *
 *   setupDefaults(){
 *     this.dom = {
 *       // ...jquery elements go here
 *     }
 *   }
 *
 *   addListeners(){
 *     // ...listeners go here
 *   }
 * }
 *
 *
 * // Create a new Foo component with some configuration
 * new Foo('.foo', {
 *   name: 'Foo'
 * })
 */
class Component {
  /**
   * Component constructor
   *
   * @param {string|Object} el - Main DOM element, you can pass a string such as `'.foo'` __or__ a jQuery object such as `$('.foo')`
   * @param {Object} [config={ }] - Additional component configuration
   */
  constructor(el, config = {}){
    if (typeof el === 'undefined') {
      throw new Error('You must provide an element as a String type or a jQuery object type');
    }

    /**
     * Main class element, this will be a jQuery instance
     *
     * @type {Object}
     */
    this.$el = el instanceof jQuery ? el : $(el);

    if (this.$el.length === 0) return;

    domTree.set(this, {});
    configuration.set(this, config);

    if (this.config.hasOwnProperty('dom')) {
      this.dom = this.config.dom;
    }

    this.setupDefaults();
    this.addListeners();
  }

  /**
   * This method is used for override;
   * It's called directly after the element and configuration have been set up
   */
  setupDefaults(){}

  /**
   * This method is used for override;
   * It's called directly after `setupDefaults()`, so everything is ready and set up at this point.
   */
  addListeners(){}

  /**
   * Get component configuration
   *
   * @example
   * this.config
   *
   * @type {Object}
   */
  get config(){
    return configuration.get(this);
  }

  /**
   * Get DOM object
   *
   * @example
   * this.dom
   *
   * @type {Object}
   */
  get dom(){
    return domTree.get(this);
  }

  /**
   * Set DOM object
   *
   * @example
   * this.dom = {
   *   $foo: this.$el.find('.foo')
   * };
   *
   * @type {Object}
   */
  set dom(elements){
    elements = {
      ...this.dom,
      ...elements
    };

    domTree.set(this, elements);
  }
}

export default Component;



// WEBPACK FOOTER //
// ./src/js/components/Component.js